// Generic HID input report decoding from the report descriptor WebHID exposes
// (HIDDevice.collections). Rather than hard-coding the G27's byte layout we
// read it from the device, so other wheels and firmware revisions decode too.

const PAGE_GENERIC_DESKTOP = 0x01;
const PAGE_SIMULATION = 0x02;
const PAGE_BUTTON = 0x09;
const USAGE_HAT = 0x39;
/** Generic Desktop axis usages: X, Y, Z, Rx, Ry, Rz, Slider, Dial, Wheel. */
const AXIS_USAGES = new Set([0x30, 0x31, 0x32, 0x33, 0x34, 0x35, 0x36, 0x37, 0x38]);

export interface Field {
  /** Extended usage: page << 16 | id. */
  usage: number;
  bitOffset: number;
  bitSize: number;
  logicalMin: number;
  logicalMax: number;
}

export interface ReportLayout {
  reportId: number;
  /** Axes in descriptor order (X first for a wheel). */
  axes: Field[];
  /** Buttons by usage id order. */
  buttons: Field[];
  hat: Field | undefined;
  /** Total bits in the report (excluding the report ID). */
  bits: number;
}

export interface RawControls {
  /** Axis values normalised to 0..1 over the logical range. */
  axes: number[];
  buttons: boolean[];
  /** Hat direction 0..7 clockwise from up, or -1 when centred. */
  hat: number;
}

export const usagePage = (usage: number): number => usage >>> 16;
export const usageId = (usage: number): number => usage & 0xffff;

function isAxis(usage: number): boolean {
  const page = usagePage(usage);
  // Simulation controls (accelerator 0xc4, brake 0xc5, clutch 0xc6, steering 0xc8) count as axes too.
  return (page === PAGE_GENERIC_DESKTOP && AXIS_USAGES.has(usageId(usage))) || (page === PAGE_SIMULATION && usageId(usage) >= 0xb0);
}

/** Build a decoder layout for every input report of the device. */
export function layoutsFrom(collections: readonly HIDCollectionInfo[]): Map<number, ReportLayout> {
  const out = new Map<number, ReportLayout>();
  const visit = (c: HIDCollectionInfo): void => {
    for (const r of c.inputReports ?? []) {
      const id = r.reportId ?? 0;
      const layout = out.get(id) ?? { reportId: id, axes: [], buttons: [], hat: undefined, bits: 0 };
      for (const item of r.items ?? []) addItem(layout, item);
      out.set(id, layout);
    }
    for (const child of c.children ?? []) visit(child);
  };
  for (const c of collections) visit(c);
  for (const l of out.values()) l.buttons.sort((a, b) => a.usage - b.usage);
  return out;
}

function addItem(layout: ReportLayout, item: HIDReportItem): void {
  const size = item.reportSize ?? 0;
  const count = item.reportCount ?? 0;
  const lmin = item.logicalMinimum ?? 0;
  const lmax = item.logicalMaximum ?? (size >= 31 ? 0x7fffffff : (1 << size) - 1);
  const usages = item.usages ?? [];
  for (let i = 0; i < count; i++) {
    const offset = layout.bits;
    layout.bits += size;
    if (item.isArray) continue; // array (selector) items: not used by wheels
    let usage: number | undefined;
    if (item.isRange && item.usageMinimum !== undefined) {
      usage = Math.min(item.usageMinimum + i, item.usageMaximum ?? Infinity);
    } else if (usages.length) {
      usage = usages[Math.min(i, usages.length - 1)];
    }
    if (usage === undefined) continue; // padding
    const field: Field = { usage, bitOffset: offset, bitSize: size, logicalMin: lmin, logicalMax: lmax };
    if (usagePage(usage) === PAGE_BUTTON) layout.buttons.push(field);
    else if (usagePage(usage) === PAGE_GENERIC_DESKTOP && usageId(usage) === USAGE_HAT) layout.hat = field;
    else if (isAxis(usage) && !layout.axes.some((a) => a.usage === usage)) layout.axes.push(field);
  }
}

/** Read `size` bits starting at bit `offset` (HID packs fields little-endian, LSB first). */
export function readBits(data: DataView, offset: number, size: number, signed: boolean): number {
  let v = 0;
  for (let i = 0; i < size; i++) {
    const bit = offset + i;
    const byte = bit >> 3;
    if (byte >= data.byteLength) break;
    if ((data.getUint8(byte) >> (bit & 7)) & 1) v += 2 ** i;
  }
  if (signed && size > 0 && v >= 2 ** (size - 1)) v -= 2 ** size;
  return v;
}

function readField(data: DataView, f: Field): number {
  return readBits(data, f.bitOffset, f.bitSize, f.logicalMin < 0);
}

export function decode(layout: ReportLayout, data: DataView, out?: RawControls): RawControls {
  const result = out ?? { axes: [], buttons: [], hat: -1 };
  result.axes.length = layout.axes.length;
  layout.axes.forEach((f, i) => {
    const span = f.logicalMax - f.logicalMin;
    result.axes[i] = span > 0 ? Math.min(1, Math.max(0, (readField(data, f) - f.logicalMin) / span)) : 0;
  });
  result.buttons.length = layout.buttons.length;
  layout.buttons.forEach((f, i) => {
    result.buttons[i] = readField(data, f) !== 0;
  });
  if (layout.hat) {
    const v = readField(data, layout.hat);
    const { logicalMin, logicalMax } = layout.hat;
    result.hat = v >= logicalMin && v <= logicalMax ? Math.round(((v - logicalMin) * 8) / (logicalMax - logicalMin + 1)) : -1;
  } else {
    result.hat = -1;
  }
  return result;
}

/** Short label for an axis usage, for the setup screen. */
export function axisLabel(usage: number): string {
  if (usagePage(usage) === PAGE_SIMULATION) {
    return ({ 0xc4: 'Accelerator', 0xc5: 'Brake', 0xc6: 'Clutch', 0xc8: 'Steering' } as Record<number, string>)[usageId(usage)] ?? `Sim ${usageId(usage).toString(16)}`;
  }
  return ['X', 'Y', 'Z', 'Rx', 'Ry', 'Rz', 'Slider', 'Dial', 'Wheel'][usageId(usage) - 0x30] ?? `Axis ${usageId(usage).toString(16)}`;
}
